import React, { useMemo, useState } from "react";
import {
  Box, Typography, Avatar, Button, TextField, CircularProgress,
  InputAdornment, IconButton, Chip, Stack, Divider, alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import {
  useGetTeamQuery, useCreateTeamMutation,
  useChangeUserPasswordSystemMutation,
} from "../../Api/attoDeskApi";
import { ITeam } from "../../Api/Interface/api.interface";
import { useNotifier } from "../../Core/Notifier";
import useUser from "../../Core/hook/useUser";
import dayjs from "dayjs";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const capitalizeFirstLetter = (s: string | null | undefined) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

// ── Section Card ──
const SectionCard: React.FC<{
  title: string; icon: React.ReactNode; color: string;
  action?: React.ReactNode; children: React.ReactNode;
}> = ({ title, icon, color, action, children }) => (
  <Box sx={{
    bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.03)", overflow: "hidden",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: alpha(color, 0.08), display: "flex", alignItems: "center", justifyContent: "center", color, "& svg": { fontSize: 17 } }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
      </Box>
      {action}
    </Box>
    <Box sx={{ px: 2.5, py: 2.5 }}>{children}</Box>
  </Box>
);

// ── Info Row ──
const InfoRow: React.FC<{ label: string; value: string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
    {icon && <Box sx={{ color: "text.disabled", display: "flex", "& svg": { fontSize: 18 } }}>{icon}</Box>}
    <Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: value ? "text.primary" : "text.disabled" }}>{value || "Not set"}</Typography>
    </Box>
  </Box>
);

const StaffProfilePage = () => {
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const { staffId: authStaffId, userData } = useUser();

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Fetch staff data
  const { data: teamData, isLoading, refetch } = useGetTeamQuery({ searchText: "", id: authStaffId ? String(authStaffId) : "" });
  const [updateTeam, { isLoading: isSaving }] = useCreateTeamMutation();
  const [changePassword, { isLoading: isChangingPw }] = useChangeUserPasswordSystemMutation();

  const staff: ITeam | null = useMemo(() => {
    if (!teamData?.data) return null;
    const list = Array.isArray(teamData.data) ? teamData.data : [teamData.data];
    if (authStaffId) return (list as ITeam[]).find((t) => t.id === authStaffId) || (list[0] as ITeam) || null;
    return (list[0] as ITeam) || null;
  }, [teamData, authStaffId]);

  const fullName = staff
    ? `${capitalizeFirstLetter(staff.teamFirstName)} ${staff.middle_name ? capitalizeFirstLetter(staff.middle_name) + " " : ""}${capitalizeFirstLetter(staff.teamLastName)}`
    : userData?.staff || "Staff";

  // ── Profile edit form ──
  const profileFormik = useFormik({
    initialValues: {
      teamFirstName: staff?.teamFirstName || "",
      teamLastName: staff?.teamLastName || "",
      middle_name: staff?.middle_name || "",
      teamEmail: staff?.teamEmail || "",
      teamMobileNum: staff?.teamMobileNum || "",
      teamAltMobileNum: staff?.teamAltMobileNum || "",
      teamCity: staff?.teamCity || "",
      teamAddress: staff?.teamAddress || "",
      teamStreet: staff?.teamStreet || "",
      teamPostalCode: staff?.teamPostalCode || "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!staff) return;
      try {
        const payload = { id: staff.id, ...values };
        const response: any = await updateTeam(payload).unwrap();
        if (response.status) {
          showMessage("Profile updated successfully");
          setIsEditingProfile(false);
          refetch();
        } else {
          showErrorMessage(response.message || "Failed to update");
        }
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Failed to update profile");
      }
    },
  });

  // ── Password form ──
  const passwordFormik = useFormik({
    initialValues: { new_password: "", confirmPassword: "" },
    validate: (values) => {
      const errors: any = {};
      if (!values.new_password) errors.new_password = "Required";
      else if (values.new_password.length < 6) errors.new_password = "Minimum 6 characters";
      if (values.new_password !== values.confirmPassword) errors.confirmPassword = "Passwords do not match";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const response: any = await changePassword({
          userId: "",
          new_password: values.new_password,
          new_password_confirmation: values.confirmPassword,
        }).unwrap();
        if (response.status) {
          showMessage("Password changed successfully");
          resetForm();
          setIsEditingPassword(false);
          if (response.token) {
            localStorage.setItem("token", response.token);
          }
        } else {
          showErrorMessage(response.message || "Failed to change password");
        }
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Failed to change password");
      }
    },
  });

  const pwValid = passwordFormik.values.new_password.length >= 6 &&
    passwordFormik.values.new_password === passwordFormik.values.confirmPassword;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh", mx: -3, mt: -3 }}>
      {/* ══════════ PROFILE HEADER ══════════ */}
      <Box sx={{
        background: "linear-gradient(135deg, #0d1b2a 0%, #1b263b 60%, #243447 100%)",
        borderRadius: "16px", p: { xs: 2.5, md: 3.5 }, mb: 3,
        position: "relative", overflow: "hidden",
        "&::before": { content: '""', position: "absolute", top: 0, right: 0, width: "40%", height: "100%", background: "radial-gradient(ellipse at top right, rgba(59,130,246,0.12), transparent 70%)", pointerEvents: "none" },
      }}>
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, gap: 2.5, position: "relative", zIndex: 1, flexDirection: { xs: "column", md: "row" } }}>
          <Avatar
            src={staff?.teamImg || undefined}
            sx={{
              width: 72, height: 72, fontSize: 28, fontWeight: 800,
              bgcolor: alpha("#3b82f6", 0.3), color: "#fff",
              border: "3px solid rgba(255,255,255,0.2)",
            }}
          >
            {fullName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
              {fullName}
            </Typography>
            <Stack direction="row" spacing={1} mt={0.75} flexWrap="wrap">
              {userData?.user_role_name && (
                <Chip label={userData.user_role_name} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.1)" }} />
              )}
              {staff?.teamEmail && (
                <Chip icon={<EmailOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.6) !important" }} />}
                  label={staff.teamEmail} size="small"
                  sx={{ height: 24, fontSize: 11, fontWeight: 500, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)", "& .MuiChip-label": { px: 0.75 } }} />
              )}
              {staff?.teamMobileNum && (
                <Chip icon={<PhoneOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.6) !important" }} />}
                  label={staff.teamMobileNum} size="small"
                  sx={{ height: 24, fontSize: 11, fontWeight: 500, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)", "& .MuiChip-label": { px: 0.75 } }} />
              )}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* ══════════ MAIN CONTENT ══════════ */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        {/* ── Personal Information ── */}
        <SectionCard title="Personal Information" icon={<PersonOutlineIcon />} color="#6366f1"
          action={
            !isEditingProfile ? (
              <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => setIsEditingProfile(true)}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "8px" }}>
                Edit
              </Button>
            ) : null
          }
        >
          {isEditingProfile ? (
            <form onSubmit={profileFormik.handleSubmit}>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="First Name" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamFirstName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  <TextField label="Middle Name" size="small" fullWidth
                    {...profileFormik.getFieldProps("middle_name")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  <TextField label="Last Name" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamLastName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Email" size="small" fullWidth type="email"
                    {...profileFormik.getFieldProps("teamEmail")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  <TextField label="Phone" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamMobileNum")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Alt. Phone" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamAltMobileNum")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  <TextField label="City" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamCity")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                </Stack>
                <TextField label="Address" size="small" fullWidth
                  {...profileFormik.getFieldProps("teamAddress")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Street" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamStreet")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  <TextField label="Postal Code" size="small" fullWidth
                    {...profileFormik.getFieldProps("teamPostalCode")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end" pt={1}>
                  <Button size="small" variant="outlined" onClick={() => { profileFormik.resetForm(); setIsEditingProfile(false); }}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "10px" }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" type="submit" disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "10px", px: 2.5, boxShadow: "none" }}>
                    Save Changes
                  </Button>
                </Stack>
              </Stack>
            </form>
          ) : (
            <Stack divider={<Divider />}>
              <InfoRow label="Full Name" value={fullName} icon={<PersonOutlineIcon />} />
              <InfoRow label="Email" value={staff?.teamEmail || ""} icon={<EmailOutlinedIcon />} />
              <InfoRow label="Phone" value={staff?.teamMobileNum || ""} icon={<PhoneOutlinedIcon />} />
              {staff?.teamAltMobileNum && <InfoRow label="Alt. Phone" value={staff.teamAltMobileNum} icon={<PhoneOutlinedIcon />} />}
              <InfoRow label="Address" value={[staff?.teamAddress, staff?.teamStreet, staff?.teamCity, staff?.teamPostalCode].filter(Boolean).join(", ")} icon={<LocationOnOutlinedIcon />} />
              {staff?.gender && <InfoRow label="Gender" value={staff.gender} icon={<BadgeOutlinedIcon />} />}
            </Stack>
          )}
        </SectionCard>

        {/* ── Right Column ── */}
        <Stack spacing={3}>
          {/* ── Change Password ── */}
          <SectionCard title="Change Password" icon={<LockOutlinedIcon />} color="#f59e0b">
            {isEditingPassword ? (
              <form onSubmit={passwordFormik.handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="New Password" size="small" fullWidth
                    type={showNewPw ? "text" : "password"}
                    {...passwordFormik.getFieldProps("new_password")}
                    error={passwordFormik.touched.new_password && Boolean(passwordFormik.errors.new_password)}
                    helperText={passwordFormik.touched.new_password && passwordFormik.errors.new_password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNewPw(!showNewPw)}>
                            {showNewPw ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                  <TextField
                    label="Confirm Password" size="small" fullWidth
                    type={showConfirmPw ? "text" : "password"}
                    {...passwordFormik.getFieldProps("confirmPassword")}
                    error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                    helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirmPw(!showConfirmPw)}>
                            {showConfirmPw ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                  {/* Password match indicator */}
                  {passwordFormik.values.new_password && passwordFormik.values.confirmPassword && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 15, color: pwValid ? "#22c55e" : "#ef4444" }} />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: pwValid ? "#22c55e" : "#ef4444" }}>
                        {pwValid ? "Passwords match" : "Passwords do not match"}
                      </Typography>
                    </Box>
                  )}
                  <Stack direction="row" spacing={1} justifyContent="flex-end" pt={0.5}>
                    <Button size="small" variant="outlined" onClick={() => { passwordFormik.resetForm(); setIsEditingPassword(false); }}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "10px" }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" type="submit" disabled={isChangingPw || !pwValid}
                      startIcon={isChangingPw ? <CircularProgress size={14} color="inherit" /> : <LockOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "10px", px: 2.5, boxShadow: "none" }}>
                      Update Password
                    </Button>
                  </Stack>
                </Stack>
              </form>
            ) : (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: alpha("#f59e0b", 0.06), display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                  <LockOutlinedIcon sx={{ fontSize: 24, color: alpha("#f59e0b", 0.4) }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>Password & Security</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                  Change your password to keep your account secure
                </Typography>
                <Button size="small" variant="contained" onClick={() => setIsEditingPassword(true)}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: "10px", px: 3, boxShadow: "none", bgcolor: "#f59e0b", "&:hover": { bgcolor: "#d97706" } }}>
                  Change Password
                </Button>
              </Box>
            )}
          </SectionCard>

          {/* ── Account Info ── */}
          <SectionCard title="Account Details" icon={<BadgeOutlinedIcon />} color="#0ea5e9">
            <Stack divider={<Divider />}>
              <InfoRow label="Username" value={userData?.username || ""} icon={<PersonOutlineIcon />} />
              <InfoRow label="Role" value={userData?.user_role_name || ""} icon={<BadgeOutlinedIcon />} />
              <InfoRow label="Account Type" value={userData?.user_type === "parent" ? "Parent" : "Employee"} icon={<BadgeOutlinedIcon />} />
              {userData?.location && <InfoRow label="Campus Location" value={userData.location} icon={<LocationOnOutlinedIcon />} />}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </Box>
  );
};

export default StaffProfilePage;
